import { useEffect, useMemo, useState } from "react";
import {
  fetchTradingViewSymbol,
  normalizeBaseSymbolForTradingView,
} from "./tradingViewSymbolSearch";

export interface UseTradingViewSymbolResult {
  tradingViewSymbol: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves a user/base symbol to a TradingView `EXCHANGE:PAIR` string (no React Query — works in Next without a provider).
 */
export function useTradingViewSymbol(
  symbol?: string | null
): UseTradingViewSymbolResult {
  const normalizedInput = useMemo(
    () => symbol?.trim().toUpperCase() ?? "",
    [symbol]
  );
  const searchKey = useMemo(
    () =>
      normalizedInput.length > 0
        ? normalizeBaseSymbolForTradingView(normalizedInput)
        : "",
    [normalizedInput]
  );

  const [tradingViewSymbol, setTradingViewSymbol] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!searchKey) {
      setTradingViewSymbol(null);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setTradingViewSymbol(null);

    fetchTradingViewSymbol(normalizedInput)
      .then((match) => {
        if (cancelled) return;
        if (!match) {
          setTradingViewSymbol(null);
          setIsError(true);
        } else {
          setTradingViewSymbol(`${match.exchange}:${match.symbol}`);
          setIsError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTradingViewSymbol(null);
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedInput, searchKey]);

  return {
    tradingViewSymbol,
    isLoading,
    isError,
  };
}
